import React from 'react';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button asChild variant="outline" className="mb-6">
          <a href="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">About Us</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Who We Are</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CompareBeforeYouBuy is an independent smartphone comparison platform dedicated to helping consumers make informed purchasing decisions. We analyze and compare the latest mobile devices from leading manufacturers, providing transparent, unbiased information about specifications, features, and pricing from multiple retailers.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our mission is simple: to simplify the smartphone buying process by providing comprehensive comparisons, expert analysis, and clear recommendations - all in one place.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Approach</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We believe in complete transparency. As an affiliate comparison platform:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>We don't sell phones directly</strong> - We compare products from multiple retailers</li>
              <li><strong>We maintain editorial independence</strong> - Our recommendations are based on specifications, features, and value, not commission rates</li>
              <li><strong>We disclose our business model</strong> - We earn commissions when you purchase through our affiliate links, at no extra cost to you</li>
              <li><strong>We provide honest comparisons</strong> - Both pros and cons of each device are clearly presented</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What We Do</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Comprehensive Comparisons</h3>
                <p className="text-gray-700">
                  We analyze smartphones across multiple dimensions: specifications, performance, camera quality, battery life, build quality, and value for money. Our side-by-side comparisons help you understand exactly what you're getting.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Independent Research</h3>
                <p className="text-gray-700">
                  Our team researches official manufacturer specifications, reads customer reviews from verified purchasers, and tracks pricing across multiple UK retailers to give you accurate, up-to-date information.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Price Tracking</h3>
                <p className="text-gray-700">
                  We monitor prices from various retailers to help you find the best deals. While prices change frequently, we work to keep our information current and clearly indicate when prices were last verified.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Buying Guides</h3>
                <p className="text-gray-700">
                  Beyond comparisons, we create helpful buying guides to explain technical features, help you understand what specifications matter for your usage, and guide you toward the right choice for your needs and budget.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Revenue Disclosure</h2>
            <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>How We Make Money:</strong> CompareBeforeYouBuy earns revenue through affiliate marketing. When you click on a product link and make a purchase from a partner retailer, that retailer may pay us a commission. This does not increase the price you pay.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Our Commitment:</strong> We maintain strict editorial independence. Affiliate partnerships do not influence our product rankings, recommendations, or the content of our comparisons. We compare products based on their merit, not commission potential.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Editorial Standards</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our editorial process is guided by:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Accuracy</strong> - We verify specifications with official manufacturer sources</li>
              <li><strong>Transparency</strong> - We clearly disclose our affiliate relationships and business model</li>
              <li><strong>Objectivity</strong> - We present both advantages and disadvantages of each product</li>
              <li><strong>Currency</strong> - We update information regularly to reflect current availability and pricing</li>
              <li><strong>Accessibility</strong> - We make our corrections policy clear and respond to user feedback</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              See our <a href="/editorial-policy" className="text-blue-600 hover:underline">Editorial Policy</a> for full details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Trust Us?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We understand that choosing a smartphone is a significant investment. That's why we:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Provide detailed, specification-based comparisons</li>
              <li>Disclose all affiliate relationships upfront</li>
              <li>Update our content regularly to maintain accuracy</li>
              <li>Offer clear, jargon-free explanations of technical features</li>
              <li>Welcome feedback and corrections from our users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Have questions, suggestions, or spotted an error? We'd love to hear from you.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <Phone className="w-8 h-8 text-blue-600 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Phone (UK Support)</h3>
                  <a href="tel:08006521866" className="text-blue-600 hover:underline text-lg">
                    08006521866
                  </a>
                  <p className="text-sm text-gray-600 mt-2">Mon-Fri: 9am-6pm, Sat: 10am-4pm</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <Mail className="w-8 h-8 text-blue-600 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Email</h3>
                  <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline break-all">
                    contact@comparebeforeyoubuy.shop
                  </a>
                  <p className="text-sm text-gray-600 mt-2">We typically respond within 24 hours</p>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Promise</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We're committed to being your trusted source for smartphone comparisons. Whether you're upgrading to the latest flagship or finding the best value option, we're here to provide clear, honest, and helpful information to guide your decision.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Thank you for choosing CompareBeforeYouBuy.
            </p>
          </section>

          {/* CTA */}
          <div className="mt-12 text-center bg-gray-900 text-white p-12 rounded-lg">
            <h2 className="text-3xl font-bold mb-4">Start Comparing Smartphones</h2>
            <p className="text-lg mb-6 text-gray-300">
              Browse our latest smartphone comparisons and find your perfect match.
            </p>
            <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
              <a href="/">
                View All Comparisons
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
